// import node module libraries
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
// import sub components
import CourseCard from "./CourseCard";
import { PATH_DASHBOARD } from "routes/Paths";

const CourseSlider = ({ recommended, popular, trending, category, courses }) => {
  const settings = {
    infinite: courses?.length > 4,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    dots: false,
    speed: 500,
    cssEase: "ease-in-out",
    autoplay: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          infinite: courses?.length > 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: courses?.length > 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: courses?.length > 2,
        },
      },
      {
        breakpoint: 540,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: courses?.length > 1,
        },
      },
    ],
  };

  return (
    <Fragment>
      <div className="course-slider-container">
        {courses?.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="fe fe-inbox"></i>
            </div>
            <h4 className="empty-state-title">No Courses Available</h4>
            <p className="empty-state-text">
              Check back soon for new courses in this category.
            </p>
          </div>
        ) : courses?.length === 1 ? (
          <div className="single-course-wrapper">
            <div className="single-course-item">
              <CourseCard 
                course={courses[0]} 
                link={PATH_DASHBOARD.courses.view(courses[0].guid)} 
              />
            </div>
          </div>
        ) : courses?.length < 4 ? (
          <div className="few-courses-grid">
            {courses.map((item) => (
              <div className="course-grid-item" key={item.id}>
                <CourseCard 
                  course={item} 
                  link={PATH_DASHBOARD.courses.view(item.guid)} 
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="slider-wrapper">
            <Slider {...settings}>
              {courses.map((item) => (
                <div className="slider-item" key={item.id}>
                  <div className="slider-item-inner">
                    <CourseCard 
                      course={item} 
                      link={PATH_DASHBOARD.courses.view(item.guid)} 
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>

      <style jsx>{`
        /* Container Styles */
        .course-slider-container {
          position: relative;
          width: 100%;
          padding: 0 8px;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: #f8f9fa;
          border-radius: 16px;
          border: 2px dashed #dee2e6;
        }

        .empty-state-icon {
          font-size: 4rem;
          color: #adb5bd;
          margin-bottom: 1.5rem;
        }

        .empty-state-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .empty-state-text {
          font-size: 1rem;
          color: #64748b;
          margin: 0;
        }

        /* Single Course Layout */
        .single-course-wrapper {
          display: flex;
          justify-content: center;
          padding: 1rem 0;
        }

        .single-course-item {
          width: 100%;
          max-width: 380px;
        }

        /* Few Courses Grid (2-3 courses) */
        .few-courses-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          padding: 1rem 0;
          max-width: 1200px;
        }

        .course-grid-item {
          width: 100%;
        }

        /* Slider Wrapper */
        .slider-wrapper {
          position: relative;
          padding: 1rem 0 2rem;
          margin: 0 -8px;
        }

        .slider-item {
          padding: 0 8px;
          height: 100%;
        }

        .slider-item-inner {
          height: 100%;
        }

        /* Slick Slider Customization */
        :global(.slick-slider) {
          position: relative;
          display: block;
          box-sizing: border-box;
          user-select: none;
          touch-action: pan-y;
        }

        :global(.slick-list) {
          position: relative;
          overflow: hidden;
          display: block;
          margin: 0;
          padding: 0;
        }

        :global(.slick-track) {
          position: relative;
          left: 0;
          top: 0;
          display: flex;
          margin-left: auto;
          margin-right: auto;
        }

        :global(.slick-slide) {
          float: left;
          height: 100%;
          min-height: 1px;
          display: flex;
        }

        :global(.slick-slide > div) {
          width: 100%;
          height: 100%;
        }

        /* Slick Dots */
        :global(.slick-dots) {
          position: absolute;
          bottom: -32px;
          display: flex !important;
          justify-content: center;
          align-items: center;
          width: 100%;
          padding: 0;
          margin: 0;
          list-style: none;
          gap: 8px;
        }

        :global(.slick-dots li) {
          margin: 0;
          padding: 0;
        }

        :global(.slick-dots li button) {
          width: 10px;
          height: 10px;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: #cbd5e1;
          cursor: pointer;
          font-size: 0;
          transition: all 0.3s ease;
        }

        :global(.slick-dots li button:hover) {
          background: #94a3b8;
          transform: scale(1.2);
        }

        :global(.slick-dots li.slick-active button) {
          width: 28px;
          border-radius: 5px;
          background: #F68521;
        }

        /* Responsive Adjustments */
        @media (max-width: 1400px) {
          .few-courses-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 1.25rem;
          }
        }

        @media (max-width: 768px) {
          .course-slider-container {
            padding: 0 4px;
          }

          .slider-wrapper {
            margin: 0 -4px;
          }

          .slider-item {
            padding: 0 4px;
          }

          .few-courses-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .empty-state {
            padding: 3rem 1.5rem;
          }

          .empty-state-icon {
            font-size: 3rem;
          }

          .empty-state-title {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </Fragment>
  );
};

// Custom Arrow Components
const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <button
      className={`custom-arrow custom-arrow-next ${className}`}
      onClick={onClick}
      aria-label="Next slide"
    >
      <i className="fe fe-chevron-right"></i>
      <style jsx>{`
        .custom-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          border: 2px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .custom-arrow:hover {
          background: #F68521;
          border-color: #F68521;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.3);
        }

        .custom-arrow:hover :global(i) {
          color: white;
        }

        .custom-arrow-next {
          right: -24px;
        }

        .custom-arrow :global(i) {
          font-size: 20px;
          color: #64748b;
          transition: color 0.3s ease;
        }

        .custom-arrow:global(.slick-disabled) {
          opacity: 0.3;
          cursor: not-allowed;
          pointer-events: none;
        }

        @media (max-width: 1024px) {
          .custom-arrow-next {
            right: -16px;
          }
        }

        @media (max-width: 768px) {
          .custom-arrow {
            display: none !important;
          }
        }
      `}</style>
    </button>
  );
};

const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <button
      className={`custom-arrow custom-arrow-prev ${className}`}
      onClick={onClick}
      aria-label="Previous slide"
    >
      <i className="fe fe-chevron-left"></i>
      <style jsx>{`
        .custom-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          border: 2px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .custom-arrow:hover {
          background: #F68521;
          border-color: #F68521;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.3);
        }

        .custom-arrow:hover :global(i) {
          color: white;
        }

        .custom-arrow-prev {
          left: -24px;
        }

        .custom-arrow :global(i) {
          font-size: 20px;
          color: #64748b;
          transition: color 0.3s ease;
        }

        .custom-arrow:global(.slick-disabled) {
          opacity: 0.3;
          cursor: not-allowed;
          pointer-events: none;
        }

        @media (max-width: 1024px) {
          .custom-arrow-prev {
            left: -16px;
          }
        }

        @media (max-width: 768px) {
          .custom-arrow {
            display: none !important;
          }
        }
      `}</style>
    </button>
  );
};

// Specifies the default values for props
CourseSlider.defaultProps = {
  recommended: false,
  popular: false,
  trending: false,
  category: null,
  courses: [],
};

// Typechecking With PropTypes
CourseSlider.propTypes = {
  recommended: PropTypes.bool,
  popular: PropTypes.bool,
  trending: PropTypes.bool,
  category: PropTypes.string,
  courses: PropTypes.array,
};

export default CourseSlider;